/**
 * KonvaMapContainer - Container Component
 *
 * Container component responsible for:
 * - Zustand store connection (useStorePrimitive, useActions)
 * - Reading drawingMode and providing setDrawingMode callback
 * - Forwarding ref through to the KonvaMap presenter for imperative API
 *
 * This follows the Container/Presenter pattern as defined in ADR-034.
 * @see docs/adr/0034-container-presenter-component-pattern.md
 */

import React from 'react';
import { useStorePrimitive, useActions } from '../../hooks/useStore';
import KonvaMap from './KonvaMap';
import type Konva from 'konva';

interface RenderedImageRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface KonvaMapContainerProps {
  containerSize: { width: number; height: number; } | null;
  imageUrl: string | null;
  onImageRectChange: (rect: RenderedImageRect | null) => void;
}

export interface KonvaMapRef {
  getStage: () => Konva.Stage;
  resetView: () => void;
  fitImage: () => void;
}

const KonvaMapContainer = React.forwardRef<KonvaMapRef, KonvaMapContainerProps>(
  (props, ref) => {
    const drawingMode = useStorePrimitive(state => state.session?.map.drawingMode);
    const { setDrawingMode } = useActions();

    return (
      <KonvaMap
        ref={ref}
        drawingMode={drawingMode ?? null}
        setDrawingMode={setDrawingMode}
        {...props}
      />
    );
  }
);

KonvaMapContainer.displayName = 'KonvaMapContainer';

export default KonvaMapContainer;
